/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import {
    withStyles,
    Grid,
    Typography,
    Paper,
    ExpansionPanel,
    ExpansionPanelSummary,
    ExpansionPanelDetails,
} from '@material-ui/core';
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { FormDetails, getFormDetails } from '../form-details-reducer';
import { getDisplayDate } from '../../../../../shared-functions';
import { OpenModal, openModalAction } from '../../../../../form-modal/actions';
import { UpdateFormDetails, updateFormDetailsAction } from '../actions/update-form-details';
import { getClaim } from '../../../../../claim';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Nullable } from '../../../../../shared-types';
import { ClaimType } from '../../../../../claim/claim-types';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
    updateFormDetails?: UpdateFormDetails;
    claimType?: Nullable<ClaimType>;
}

interface Props extends BaseProps, StyledComponent {
    readonly openModal: OpenModal;
}

const incomeHistoryView: React.FC<Props> = ({
    classes,
    openModal,
    formDetails,
    updateFormDetails,
    disableEditButton,
    claimType,
}: Props) => {
    if (claimType === ClaimType.Retrenchment) {
        return null;
    }
    const employment = formDetails?.employment;
    const income = employment?.incomeDetails;

    const hasValidIncome =
        income?.sourcesOfIncome?.some(
            item => item.date !== null || item.incomeAmount !== null || item.sourceOfIncome !== null
        ) ?? false;

    return (
        <>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={10} alignItems="center">
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        Income Details
                    </Typography>
                </Grid>
            </Grid>

            <Paper className={classes.paper}>
                {/* INCOME DETAILS */}
                <Grid container spacing={2} className={classes.marginTop2}>
                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="What was your taxable income (income before deduction) for the past 12 months?"
                            value={income?.whatWasYourTaxableIncomeForThePastTwelveMonths}
                        />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="If you are a commission-based employee, what was your commission earned during the past 4 months?"
                            value={income?.whatWasYourCommissionEarnedDuringThePastFourMonths}
                        />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="If you are a director, please provide director fees over the past 24 months"
                            value={income?.pleaseProvideDirectorFeesOverThePast24Months}
                        />
                    </Grid>

                    <Grid item xs={4} lg={3}>
                        <LabeledValue
                            label="Have you received any income or any other benefits since disablement?"
                            value={
                                income?.haveYouReceivedAnyIncomeOrAnyOtherBenefitsSinceDisablement
                            }
                        />
                    </Grid>
                    {(income?.sourcesOfIncome?.length ?? 0) > 0 && hasValidIncome && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" className={classes.heading}>
                                Source of Income
                            </Typography>
                        </Grid>
                    )}

                    {income?.haveYouReceivedAnyIncomeOrAnyOtherBenefitsSinceDisablement === 'Yes' &&
                        income?.sourcesOfIncome?.map((s, index) => (
                            <ExpansionPanel key={index} style={{ width: '100%' }}>
                                <ExpansionPanelSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`panel-content-${index}`}
                                    id={`panel-header-${index}`}
                                >
                                    <Typography>{`Source of income ${index + 1}`}</Typography>
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails>
                                    <Grid container spacing={2}>
                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Source of Income"
                                                value={s.sourceOfIncome}
                                            />
                                        </Grid>

                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Income amount"
                                                value={s.incomeAmount}
                                            />
                                        </Grid>

                                        <Grid item xs={4} lg={3}>
                                            <LabeledValue
                                                label="Date"
                                                value={getDisplayDate(s.date)}
                                            />
                                        </Grid>
                                    </Grid>
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        ))}
                </Grid>
            </Paper>
        </>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails ?? getFormDetails(state),
    disableEditButton: ownProps.disableEditButton ?? false,
    updateFormDetails: ownProps.updateFormDetails ?? updateFormDetailsAction,
    claimType: getClaim(state).claimType,
});

const mapDispatchToProps = {
    openModal: openModalAction,
    updateFormDetails: updateFormDetailsAction,
};

export const IncomeHistoryView = connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles)(incomeHistoryView));
